import {
  LegalLayout,
  Heading,
  Text,
  UnorderedList,
  ListItem,
  Link,
  RouterLink,
} from '../components/LegalLayout';

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="June 7, 2026">
      <Text>
        These Terms of Service (&ldquo;Terms&rdquo;) constitute a legally
        binding agreement between you (&ldquo;you&rdquo; or &ldquo;User&rdquo;)
        and IvoryShoulder, LLC, doing business as WagerWatchdog
        (&ldquo;WagerWatchdog,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;our&rdquo;), governing your access to and use of the
        WagerWatchdog website at wagerwatchdog.com, the application at
        app.wagerwatchdog.com, and any related services, features, content, or
        applications (collectively, the &ldquo;Services&rdquo;).
      </Text>
      <Text>
        <strong>
          By accessing or using the Services, you agree to be bound by these
          Terms. If you do not agree, you must not access or use the Services.
        </strong>
      </Text>

      <Heading as="h2">1. Eligibility</Heading>
      <Text>
        You must be at least 18 years of age to use the Services. By using the
        Services, you represent and warrant that you meet this age requirement
        and that you have the legal capacity to enter into this agreement. If you
        are using the Services on behalf of an organization, you represent and
        warrant that you have authority to bind that organization to these Terms.
      </Text>

      <Heading as="h2">2. Account Registration</Heading>
      <Text>
        To access certain features, you must create an account. You agree to:
      </Text>
      <UnorderedList>
        <ListItem>
          Provide accurate, current, and complete information during
          registration.
        </ListItem>
        <ListItem>
          Maintain and promptly update your account information to keep it
          accurate and complete.
        </ListItem>
        <ListItem>
          Maintain the security and confidentiality of your login credentials.
        </ListItem>
        <ListItem>
          Accept responsibility for all activities that occur under your account.
        </ListItem>
        <ListItem>
          Notify us immediately of any unauthorized use of your account.
        </ListItem>
      </UnorderedList>
      <Text>
        We reserve the right to suspend or terminate accounts that violate these
        Terms or that we reasonably believe are being used for unauthorized
        purposes.
      </Text>

      <Heading as="h2">3. Description of Services</Heading>
      <Text>
        WagerWatchdog provides sports betting analytics, expert performance
        tracking, and related informational tools. The Services are intended for
        informational and analytical purposes only.
      </Text>
      <Text>
        <strong>Important Disclaimers:</strong>
      </Text>
      <UnorderedList>
        <ListItem>
          WagerWatchdog does not facilitate, arrange, or encourage gambling or
          wagering of any kind.
        </ListItem>
        <ListItem>
          The Services do not constitute financial, investment, or gambling
          advice.
        </ListItem>
        <ListItem>
          We do not guarantee the accuracy, completeness, or reliability of any
          analytics, statistics, or data provided through the Services.
        </ListItem>
        <ListItem>
          You are solely responsible for any decisions you make based on
          information obtained through the Services.
        </ListItem>
        <ListItem>
          You must comply with all applicable laws regarding sports betting in
          your jurisdiction.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">4. Subscription Plans and Payment</Heading>

      <Heading as="h3">4.1 Paid Subscriptions</Heading>
      <Text>
        Certain features of the Services require a paid subscription. By
        subscribing to a paid plan, you agree to pay the applicable fees as
        described at the time of purchase. All fees are in U.S. dollars unless
        otherwise stated.
      </Text>

      <Heading as="h3">4.2 Billing</Heading>
      <Text>
        Subscription fees are billed in advance on a recurring basis (monthly or
        annually, depending on your plan). Payment is processed through our
        third-party payment processor, Stripe. You authorize us to charge your
        payment method for all applicable fees.
      </Text>

      <Heading as="h3">4.3 Cancellation</Heading>
      <Text>
        You may cancel your subscription at any time through your account
        settings or by contacting support. Upon cancellation, your access to
        paid features will continue until the end of your current billing
        period, after which your account will revert to the free tier.
      </Text>

      <Heading as="h3">4.4 Refunds</Heading>
      <Text>
        Refunds are governed by our{' '}
        <Link as={RouterLink} to="/refunds">
          Refund Policy
        </Link>
        . Please review it for details.
      </Text>

      <Heading as="h2">
        5. Prohibited Conduct and Restrictions on Use
      </Heading>
      <Text>
        You agree not to engage in, attempt to engage in, or facilitate any of
        the following prohibited activities:
      </Text>

      <Heading as="h3">5.1 Reverse Engineering and Unauthorized Access</Heading>
      <Text>
        <strong>
          You shall not, and shall not permit any third party to:
        </strong>
      </Text>
      <UnorderedList>
        <ListItem>
          Reverse engineer, decompile, disassemble, decode, or otherwise attempt
          to derive the source code, underlying algorithms, data structures, or
          trade secrets of any part of the Services.
        </ListItem>
        <ListItem>
          Modify, adapt, translate, or create derivative works based on the
          Services or any part thereof.
        </ListItem>
        <ListItem>
          Bypass, circumvent, disable, or interfere with any security features,
          access controls, authentication mechanisms, or technical protection
          measures of the Services.
        </ListItem>
        <ListItem>
          Access or use the Services through any unauthorized means, including
          but not limited to using stolen credentials, exploiting
          vulnerabilities, or engaging in brute-force attacks.
        </ListItem>
      </UnorderedList>

      <Heading as="h3">
        5.2 Scraping, Data Mining, and Automated Access
      </Heading>
      <Text>
        <strong>
          You shall not, and shall not permit any third party to:
        </strong>
      </Text>
      <UnorderedList>
        <ListItem>
          Use any automated means, including but not limited to robots, spiders,
          scrapers, crawlers, data mining tools, or similar data gathering or
          extraction methods to access, collect, copy, or monitor any portion of
          the Services or any data or content available through the Services.
        </ListItem>
        <ListItem>
          Systematically retrieve data or content from the Services to create or
          compile, directly or indirectly, a collection, compilation, database,
          or directory, whether manually or through automated means.
        </ListItem>
        <ListItem>
          Use the Services to build a competitive product or service, or to
          benchmark against a competing product or service.
        </ListItem>
        <ListItem>
          Mirror, frame, or replicate any portion of the Services on any other
          server, device, or platform.
        </ListItem>
        <ListItem>
          Access the Services at a rate that exceeds reasonable use or that
          places an undue burden on our infrastructure.
        </ListItem>
      </UnorderedList>

      <Heading as="h3">5.3 Other Prohibited Conduct</Heading>
      <UnorderedList>
        <ListItem>
          Use the Services for any unlawful purpose or in violation of any
          applicable law or regulation.
        </ListItem>
        <ListItem>
          Impersonate any person or entity, or falsely state or misrepresent your
          affiliation with a person or entity.
        </ListItem>
        <ListItem>
          Transmit any malware, viruses, worms, or other malicious or harmful
          code.
        </ListItem>
        <ListItem>
          Interfere with, disrupt, or attempt to gain unauthorized access to the
          Services, servers, or networks connected to the Services.
        </ListItem>
        <ListItem>
          Harass, threaten, or abuse other users of the Services.
        </ListItem>
        <ListItem>
          Use the Services to send unsolicited communications, spam, or
          promotional materials.
        </ListItem>
        <ListItem>
          Resell, sublicense, lease, or otherwise transfer access to the Services
          without our prior written consent.
        </ListItem>
        <ListItem>
          Remove, alter, or obscure any proprietary notices, labels, or marks on
          the Services.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">6. Remedies for Violations</Heading>
      <Text>
        Violations of Section 5 may result in one or more of the following
        remedies, at our sole discretion:
      </Text>
      <UnorderedList>
        <ListItem>
          Immediate suspension or termination of your account and access to the
          Services without notice or refund.
        </ListItem>
        <ListItem>
          Pursuit of injunctive relief to prevent continued or threatened
          violations.
        </ListItem>
        <ListItem>
          Recovery of damages, including but not limited to actual damages,
          consequential damages, lost profits, and costs of investigation and
          enforcement (including reasonable attorneys&rsquo; fees).
        </ListItem>
        <ListItem>
          Reporting of suspected illegal activity to appropriate law enforcement
          authorities, including the sharing of relevant account information.
        </ListItem>
        <ListItem>
          Any other remedies available under applicable law or in equity.
        </ListItem>
      </UnorderedList>
      <Text>
        The exercise of any remedy does not preclude the exercise of any other
        remedy. These remedies are cumulative and in addition to any other
        remedies available at law or in equity.
      </Text>

      <Heading as="h2">7. Intellectual Property</Heading>

      <Heading as="h3">7.1 Our Property</Heading>
      <Text>
        The Services, including all content, features, functionality, software,
        code, data compilations, analytics methodologies, algorithms, designs,
        text, graphics, logos, icons, images, and trademarks, are and shall
        remain the exclusive property of WagerWatchdog and its licensors. The
        Services are protected by copyright, trademark, trade secret, and other
        intellectual property laws of the United States and international
        jurisdictions.
      </Text>

      <Heading as="h3">7.2 Limited License</Heading>
      <Text>
        Subject to your compliance with these Terms, we grant you a limited,
        non-exclusive, non-transferable, non-sublicensable, revocable license to
        access and use the Services for your personal, non-commercial use (or
        authorized business use under an applicable subscription plan). This
        license does not include any right to:
      </Text>
      <UnorderedList>
        <ListItem>
          Copy, reproduce, distribute, or publicly display any part of the
          Services.
        </ListItem>
        <ListItem>
          Modify or create derivative works of the Services.
        </ListItem>
        <ListItem>
          Use the Services for commercial purposes beyond the scope of your
          subscription plan.
        </ListItem>
      </UnorderedList>

      <Heading as="h3">7.3 Feedback</Heading>
      <Text>
        If you provide any feedback, suggestions, or ideas regarding the
        Services (&ldquo;Feedback&rdquo;), you hereby assign to us all rights in
        such Feedback and agree that we may use it without restriction or
        compensation to you.
      </Text>

      <Heading as="h2">8. Disclaimer of Warranties</Heading>
      <Text>
        <strong>
          THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
          IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT.
        </strong>
      </Text>
      <Text>
        We do not warrant that the Services will be uninterrupted, error-free,
        secure, or free of viruses or other harmful components. We do not
        warrant the accuracy, completeness, or reliability of any content,
        data, or analytics provided through the Services.
      </Text>

      <Heading as="h2">9. Limitation of Liability</Heading>
      <Text>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
          WAGERWATCHDOG, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES,
          OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT
          LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, DATA, OR OTHER
          INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO
          OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICES, WHETHER BASED
          ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY
          OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
          SUCH DAMAGES.
        </strong>
      </Text>
      <Text>
        <strong>
          OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
          RELATING TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER
          OF (A) THE AMOUNTS YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING
          THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100.00).
        </strong>
      </Text>

      <Heading as="h2">10. Indemnification</Heading>
      <Text>
        You agree to indemnify, defend, and hold harmless WagerWatchdog and its
        officers, directors, employees, agents, affiliates, and licensors from
        and against any and all claims, liabilities, damages, losses, costs, and
        expenses (including reasonable attorneys&rsquo; fees) arising out of or
        in any way connected with:
      </Text>
      <UnorderedList>
        <ListItem>Your access to or use of the Services.</ListItem>
        <ListItem>Your violation of these Terms.</ListItem>
        <ListItem>
          Your violation of any rights of a third party, including any
          intellectual property or privacy right.
        </ListItem>
        <ListItem>
          Any claim that your use of the Services caused damage to a third
          party.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">11. Governing Law and Dispute Resolution</Heading>

      <Heading as="h3">11.1 Governing Law</Heading>
      <Text>
        These Terms shall be governed by and construed in accordance with the
        laws of the State of Tennessee, United States, without regard to its
        conflict of law principles.
      </Text>

      <Heading as="h3">11.2 Dispute Resolution</Heading>
      <Text>
        Any dispute, claim, or controversy arising out of or relating to these
        Terms or the Services shall first be attempted to be resolved through
        good-faith informal negotiation. If informal resolution is unsuccessful
        within 30 days, either party may initiate binding arbitration under the
        rules of the American Arbitration Association (AAA), with the
        arbitration conducted in Nashville, Tennessee.
      </Text>

      <Heading as="h3">11.3 Class Action Waiver</Heading>
      <Text>
        <strong>
          YOU AND WAGERWATCHDOG AGREE THAT EACH MAY BRING CLAIMS AGAINST THE
          OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF
          OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
        </strong>{' '}
        The arbitrator may not consolidate the claims of more than one person and
        may not preside over any form of representative or class proceeding.
      </Text>

      <Heading as="h2">12. Termination</Heading>
      <Text>
        We may suspend or terminate your access to the Services at any time, for
        any reason or no reason, with or without notice. Upon termination:
      </Text>
      <UnorderedList>
        <ListItem>
          All rights and licenses granted to you under these Terms will
          immediately cease.
        </ListItem>
        <ListItem>
          You must immediately cease all use of the Services.
        </ListItem>
        <ListItem>
          Sections 5 through 14 shall survive termination.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">13. General Provisions</Heading>

      <Heading as="h3">13.1 Entire Agreement</Heading>
      <Text>
        These Terms, together with our{' '}
        <Link as={RouterLink} to="/privacy">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link as={RouterLink} to="/refunds">
          Refund Policy
        </Link>
        , constitute the entire agreement between you and WagerWatchdog
        regarding the Services.
      </Text>

      <Heading as="h3">13.2 Severability</Heading>
      <Text>
        If any provision of these Terms is found to be invalid or unenforceable,
        the remaining provisions will remain in full force and effect.
      </Text>

      <Heading as="h3">13.3 Waiver</Heading>
      <Text>
        The failure of WagerWatchdog to enforce any right or provision of these
        Terms shall not constitute a waiver of such right or provision.
      </Text>

      <Heading as="h3">13.4 Assignment</Heading>
      <Text>
        You may not assign or transfer these Terms or your rights hereunder
        without our prior written consent. We may assign these Terms without
        restriction.
      </Text>

      <Heading as="h3">13.5 Force Majeure</Heading>
      <Text>
        WagerWatchdog shall not be liable for any failure or delay in
        performance due to circumstances beyond our reasonable control,
        including acts of God, natural disasters, war, terrorism, pandemic, labor
        disputes, government actions, or internet or power outages.
      </Text>

      <Heading as="h2">14. Changes to These Terms</Heading>
      <Text>
        We reserve the right to modify these Terms at any time. We will notify
        you of material changes by posting the revised Terms on this page with
        an updated &ldquo;Last updated&rdquo; date. For significant changes, we
        may also provide notice via email or in-app notification. Your continued
        use of the Services after any modification constitutes your acceptance of
        the revised Terms.
      </Text>

      <Heading as="h2">15. Contact Us</Heading>
      <Text>
        If you have questions about these Terms, contact us at:
      </Text>
      <Text>
        IvoryShoulder, LLC DBA WagerWatchdog
        <br />
        Email:{' '}
        <Link href="mailto:legal@wagerwatchdog.com">
          legal@wagerwatchdog.com
        </Link>
      </Text>
    </LegalLayout>
  );
}
